import { useUserAuthenticationContext, useViewportInfoContext } from "@/src/contexts";
import type { IDiscountsPanel, IDiscountsPanelResult } from "@/src/discount/DiscountsPanel/DiscountsPanel";
import { defaultAttributes } from "@Components/Component";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import type { IErrorMessage } from "@Components/ErrorMessage";
import { urlFormat } from "@Components/Modules/Helpers";
import { Device } from "@Components/Modules/ViewportResize";

import type { ICategoriesCarouselItem } from "../common/CategoriesCarousel";
import DiscountsPanel from "../discount/DiscountsPanel";

import * as css from "./ShoppingMallTabSaved.module.css";

export interface IShoppingMallTabSaved extends IComponentAjax<ICategoriesCarouselItem[]> {
    title: string;
    allFiltersText: string;
    noDiscountsText: string;
    discounts: IDiscountsPanel;
    onSelectTab: (_selectedTab: number) => void;
    isAuthenticated: boolean;
    device: Device;
}

interface IShoppingMallTabSavedState extends IComponentAjaxState {
    categories: string[];
    selectedCategory: string | null;
    noDiscounts: boolean;
}

class ShoppingMallTabSaved extends ComponentAjax<
    ICategoriesCarouselItem[],
    IShoppingMallTabSaved,
    IShoppingMallTabSavedState
> {
    private get discountsPanel(): JSX.Element {
        if (this.state.noDiscounts) {
            return <div className={css.noDiscounts}>{this.props.noDiscountsText}</div>;
        }

        return (
            <DiscountsPanel
                {...this.props.discounts}
                key={this.state.selectedCategory || ""}
                hideIfEmpty={true}
                success={this.discountsLoaded}
                fail={this.discountsFailed}
                disableLazyLoading={true}
                ajaxAction={urlFormat(this.props.discounts.ajaxAction, {
                    category: this.state.selectedCategory || "",
                    group: this.groupAlone(),
                })}
            />
        );
    }

    constructor(props: IShoppingMallTabSaved) {
        super(props);

        this.state = {
            isLoading: false,
            categories: props.initialData ? props.initialData.map((value) => value.name) : [],
            selectedCategory: null,
            noDiscounts: false,
        };
    }

    public UNSAFE_componentWillReceiveProps(nextProps: IShoppingMallTabSaved): void {
        if (this.props.device !== nextProps.device) {
            const selectedCategory =
                nextProps.device === Device.Mobile || nextProps.device === Device.Tablet
                    ? this.state.selectedCategory
                    : null;

            this.setState({ selectedCategory, noDiscounts: false });
        }

        if (nextProps.device !== Device.Mobile && nextProps.device !== Device.Tablet) {
            return;
        }

        this.fetchCategories();
    }

    public componentDidMount() {
        if (this.props.device !== Device.Mobile && this.props.device !== Device.Tablet) {
            return;
        }

        this.fetchCategories();
    }

    public render() {
        return (
            <section {...defaultAttributes(this.props, css.shoppingMallTabSavedComponent)}>
                <h3>{this.props.title}</h3>
                <div>
                    {this.discountsPanel}
                    {this.renderAjaxErrorContainer()}
                </div>
            </section>
        );
    }

    protected ajaxSuccess(response: ICategoriesCarouselItem[]): void {
        if (!response) {
            this.setState({ isLoading: false, categories: [] });
            return;
        }

        const categories = response.map((item) => item.name);

        if (this.props.allFiltersText) {
            categories.splice(0, 0, this.props.allFiltersText);
        }

        this.setState({ isLoading: false, categories });
    }

    protected ajaxFail(): boolean {
        this.setState({ isLoading: false, categories: [] });
        return true;
    }

    private fetchCategories(): void {
        if (this.state.isLoading === true || this.state.categories.length > 0) {
            return;
        }

        this.setState({ isLoading: true });

        this.ajaxRequest();
    }

    private discountsLoaded = (response: IDiscountsPanelResult) => {
        if (!response || !response.carousels || response.carousels.length === 0) {
            this.setState({ noDiscounts: true });
        }
    };

    private groupAlone = () => this.props.device !== Device.Mobile && this.props.device !== Device.Tablet;

    private discountsFailed(_error: string | IErrorMessage, xhr?: XMLHttpRequest): boolean {
        if (xhr && (xhr.status === 401 || xhr.status === 403)) {
            if (!this.props.isAuthenticated && this.props.onSelectTab) {
                this.props.onSelectTab(0);
            }
        }

        return true;
    }
}

const ShoppingMallTabSavedConnect = (props: IShoppingMallTabSaved) => {
    const { isAuthenticated } = useUserAuthenticationContext();
    const { device } = useViewportInfoContext();

    return <ShoppingMallTabSaved {...props} device={device} isAuthenticated={isAuthenticated} />;
};

export { ShoppingMallTabSavedConnect as ShoppingMallTabSaved };
